import type { NavItem } from '@suntex.core/nav';
import type { Entities_Login_StateInfo_Response } from '@suntex.fim/api-client';
import { EntityLevel, LoginType } from '@suntex.fim/enums';
import { flaggedItems, getFlags } from '@suntex.fim/flags';

import { PREVIEW_ROUTE } from './const';
import type { NavIcons } from './icons';
import { entityPath } from './utils/paths';

type NavUser = Pick<
  Entities_Login_StateInfo_Response,
  'entityId' | 'teamId' | 'schoolId' | 'entityLevel' | 'loginType'
>;

export interface NavMenuContext {
  icons: NavIcons;
  /** Display level used to build entity-scoped routes. */
  level: EntityLevel;
  /** Concrete id for the active role (team/player/etc). */
  roleId: string | number | undefined;
  user: NavUser;
  appVersion: string;
  onPlay: () => void;
}

function makeBase(level: EntityLevel, roleId: string | number | undefined) {
  return (...segments: string[]) => entityPath(level, roleId, ...segments);
}

function versionItem(icons: NavIcons, appVersion: string): NavItem {
  return {
    key: 'version',
    icon: icons.global,
    label: `Version ${appVersion}`,
  };
}

const EXPLORE_URL = 'https://explore.firstinmath.com/';
const STORE_URL = 'https://www.24game.com/';

function externalItems(icons: NavIcons): NavItem[] {
  return [
    {
      key: 'explore',
      icon: icons.exploreGo,
      label: 'Explore FIM',
      href: EXPLORE_URL,
      ariaLabel: 'Explore FIM (opens in new tab)',
    },
    {
      key: 'store',
      icon: icons.store,
      label: 'FIM Store',
      href: STORE_URL,
      ariaLabel: 'FIM Store (opens in new tab)',
    },
  ];
}

function learningCenterItem(
  icons: NavIcons,
  level: EntityLevel,
  roleId: string | number | undefined,
): NavItem {
  function lc(page: string) {
    return entityPath(level, roleId, 'learning-center', page);
  }
  return {
    key: 'learning-center',
    icon: icons.learningCenter,
    label: 'Learning Center',
    children: [
      { key: 'tutorials', label: 'Tutorials', to: lc('tutorials') },
      { key: 'resources', label: 'Resources', to: lc('resources') },
      { key: 'recognition', label: 'Recognition', to: lc('recognition') },
      {
        key: 'home-Connection',
        label: 'Home Connection',
        to: lc('home-connection'),
      },
    ],
  };
}

/** Sidebar menu for team leaders (the default context). */
export function buildTeamMenu(ctx: NavMenuContext): NavItem[] {
  const { icons, level, roleId } = ctx;
  const base = makeBase(level, roleId);
  const managementSlug =
    level === EntityLevel.Team ? 'team-management' : 'school-management';
  return [
    { key: 'home', icon: icons.home, label: 'Home', to: base('dashboard') },
    {
      key: 'suggest-content',
      icon: icons.suggestContent,
      label: 'Suggest Content',
      to: base('suggest-content/correlations'),
    },
    {
      key: 'assessment-report',
      icon: icons.assessment,
      label: 'Assessment Reports',
      to: base('assessment-report/summary'),
    },
    {
      key: 'focus',
      icon: icons.focus,
      label: 'Student Focus',
      to: base('focus'),
    },
    {
      key: 'rankings',
      icon: icons.rankings,
      label: 'Rankings',
      children: [
        { key: 'rankings-index', label: 'FIM Rankings', to: base('rankings') },
        {
          key: 'wall-of-fame-tool',
          label: 'Wall of Fame Tool',
          to: base('rankings/wall-of-fame-tool'),
        },
      ],
    },
    {
      key: 'player-management',
      icon: icons.teamManagement,
      label: 'Team Management',
      children: [
        {
          key: 'player-roster',
          label: 'Team Roster',
          to: base('team-management/team-roster'),
        },
        {
          key: 'print-logins',
          label: 'Print Logins',
          to: base('team-management/print-logins'),
        },
        {
          key: 'move-players',
          label: 'Move Players',
          to: base(`${managementSlug}/move-players`),
        },
      ],
    },
    ...(ctx.user.loginType === LoginType.TeamLeader
      ? [
          {
            key: 'Play',
            icon: icons.play,
            label: 'Play',
            children: [
              {
                key: 'play-dashboard',
                label: 'Dashboard',
                to: base('play'),
                onClick: () => ctx.onPlay(),
              },
              {
                key: 'play-content-library',
                label: 'Content Library',
                to: base('play', 'content-library'),
                onClick: () => ctx.onPlay(),
              },
            ],
          },
        ]
      : []),
    {
      key: 'Preview',
      icon: icons.preview,
      label: 'Preview',
      children: [
        { key: 'preview-dashboard', label: 'Dashboard', to: PREVIEW_ROUTE },
        {
          key: 'preview-content-library',
          label: 'Content Library',
          to: `${PREVIEW_ROUTE}/content-library`,
        },
      ],
    },
  ];
}

/** Bottom sidebar menu for team leaders. */
export function buildTeamBottomMenu(ctx: NavMenuContext): NavItem[] {
  return [
    learningCenterItem(ctx.icons, EntityLevel.Team, ctx.roleId),
    ...externalItems(ctx.icons),
    versionItem(ctx.icons, ctx.appVersion),
  ];
}

/** Sidebar menu for players. */
export function buildPlayerMenu(ctx: NavMenuContext): NavItem[] {
  const { icons, level, roleId, user } = ctx;
  const base = makeBase(level, roleId);
  const flags = getFlags();
  return [
    {
      key: 'home',
      icon: icons.home,
      label: 'Home',
      children: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: base('dashboard'),
        },
        {
          key: 'content-library',
          label: 'Content Library',
          to: base('content-library'),
        },
      ],
    },
    ...flaggedItems<NavItem>(flags.player.goals, [
      {
        key: 'goals',
        icon: icons.goal,
        label: 'Goals',
        children: [
          {
            key: 'my-goals',
            label: 'My Goals',
            to: entityPath(level, user.entityId, 'my-goals'),
          },
          {
            key: 'team-goals',
            label: 'Team Goals',
            to: entityPath(
              EntityLevel.Team,
              user.teamId,
              'assessment-report',
              'goals-index',
            ),
          },
          {
            key: 'school-goals',
            label: 'School Goals',
            to: entityPath(
              EntityLevel.School,
              user.schoolId,
              'assessment-report',
              'goals-index',
            ),
          },
        ],
      },
    ]),
    {
      key: 'rankings',
      icon: icons.rankings,
      label: 'Rankings',
      to: base('rankings'),
    },
    {
      key: 'achievements',
      icon: icons.award,
      label: 'Achievements',
      children: [
        {
          key: 'skill-level-and-awards',
          label: 'Skill Level & Awards',
          to: '/player/achievements',
        },
        ...flaggedItems<NavItem>(flags.player.quests, [
          { key: 'my-quests', label: 'My Quests', to: base('my-quests') },
        ]),
        ...flaggedItems<NavItem>(flags.player.badges, [
          { key: 'my-badges', label: 'My Badges', to: base('my-badges') },
        ]),
        ...flaggedItems<NavItem>(flags.avatarSelector, [
          { key: 'my-avatar', label: 'Edit My Avatar', to: base('my-avatar') },
        ]),
      ],
    },
    {
      key: 'player-reports',
      icon: icons.assessment,
      label: 'Reports',
      children: [
        {
          key: 'my-progress',
          label: 'My Progress',
          to: base('reports/progress'),
        },
        {
          key: 'stages-of-learning',
          label: 'Stages of Learning',
          to: base('reports/stages-of-learning'),
        },
      ],
    },
    ...(user.loginType !== LoginType.IndividualPlayer
      ? [
          {
            key: 'family-link',
            icon: icons.familyLink,
            label: 'Family Link',
            to: base('family-link'),
          },
        ]
      : []),
  ];
}

/** Sidebar menu for school principals. */
export function buildSchoolMenu(ctx: NavMenuContext): NavItem[] {
  const { icons, level, roleId } = ctx;
  function base(...segments: string[]) {
    return entityPath(level, roleId, ...segments);
  }
  return [
    { key: 'home', icon: icons.home, label: 'Home', to: base('dashboard') },
    {
      key: 'assessment-report',
      icon: icons.assessment,
      label: 'Assessment Reports',
      to: base('assessment-report/summary'),
    },
    {
      key: 'rankings',
      icon: icons.rankings,
      label: 'Rankings',
      children: [
        { key: 'rankings-index', label: 'FIM Rankings', to: base('rankings') },
        {
          key: 'top-players-teams',
          label: 'Top Players and Teams',
          to: base('rankings/top-players-teams'),
        },
        {
          key: 'wall-of-fame-tool',
          label: 'Wall of Fame Tool',
          to: base('rankings/wall-of-fame-tool'),
        },
      ],
    },
    {
      key: 'school-management',
      icon: icons.teamManagement,
      label: 'School Management',
      children: [
        {
          key: 'basic-information',
          label: 'Basic Information',
          to: base('school-management/basic-information'),
        },
        {
          key: 'manage-staff',
          label: 'Manage Staff',
          to: base('school-management/manage-staff'),
        },
        {
          key: 'assign-teachers',
          label: 'Assign Teachers',
          to: base('school-management/assign-teachers'),
        },
        {
          key: 'view-edit-teams',
          label: 'View & Edit Teams',
          to: base('school-management/view-edit-teams'),
        },
        {
          key: 'player-management',
          label: 'Player Management',
          to: base('school-management/player-management'),
        },
        {
          key: 'move-players',
          label: 'Move Players',
          to: base('school-management/move-players'),
        },
        {
          key: 'print-logins',
          label: 'Print Logins',
          to: base('school-management/print-logins'),
        },
      ],
    },
  ];
}

/** Bottom sidebar menu for school principals. */
export function buildSchoolBottomMenu(ctx: NavMenuContext): NavItem[] {
  const { icons, roleId } = ctx;
  return [
    learningCenterItem(icons, EntityLevel.School, roleId),
    ...externalItems(icons),
    {
      key: 'help',
      icon: icons.help,
      label: 'Help',
      children: [
        {
          key: 'faq',
          label: 'FAQ',
          to: entityPath(EntityLevel.School, roleId, 'help', 'faq'),
        },
      ],
    },
    versionItem(icons, ctx.appVersion),
  ];
}

/** Bottom sidebar menu for players (version only). */
export function buildPlayerBottomMenu(ctx: NavMenuContext): NavItem[] {
  return [versionItem(ctx.icons, ctx.appVersion)];
}

/** Bottom sidebar menu for district/region admins (version only). */
export function buildDistrictRegionBottomMenu(ctx: NavMenuContext): NavItem[] {
  return [versionItem(ctx.icons, ctx.appVersion)];
}

export interface MenuSet {
  top: NavItem[];
  bottom: NavItem[];
}

/** Select the appropriate top/bottom menus for the active entity level. */
export function buildMenusForLevel(ctx: NavMenuContext): MenuSet {
  switch (ctx.level) {
    case EntityLevel.Player:
      return { top: buildPlayerMenu(ctx), bottom: buildPlayerBottomMenu(ctx) };
    case EntityLevel.School:
      return { top: buildSchoolMenu(ctx), bottom: buildSchoolBottomMenu(ctx) };
    case EntityLevel.District:
    case EntityLevel.Region:
      return { top: [], bottom: buildDistrictRegionBottomMenu(ctx) };
    default:
      return { top: buildTeamMenu(ctx), bottom: buildTeamBottomMenu(ctx) };
  }
}
